(() => {
  'use strict';
  if (window.PinmeNewsletterForm) return;
  const controllers = new Map();
  const read = (storage, key) => { try { return window[storage].getItem(key); } catch (_) { return null; } };
  const write = (storage, key, value) => { try { window[storage].setItem(key, value); } catch (_) {} };
  const decodeMessage = value => String(value || '').replace(/&(?:#(\d+)|#x([\da-f]+)|(amp|apos|quot|lt|gt|nbsp));/gi, (entity, decimal, hex, named) => {
    if (named) return { amp: '&', apos: "'", quot: '"', lt: '<', gt: '>', nbsp: '\u00a0' }[named.toLowerCase()];
    const code = parseInt(decimal || hex, hex ? 16 : 10);
    return code > 0 && code <= 0x10ffff && !(code >= 0xd800 && code <= 0xdfff) ? String.fromCodePoint(code) : entity;
  });
  function enhance(root, options = {}) {
    if (controllers.has(root)) return controllers.get(root);
    let form = root.querySelector(options.formSelector || 'form');
    if (!form) return null;
    const abort = new AbortController(), on = (target, type, callback, extra = {}) => target.addEventListener(type, callback, { ...extra, signal: abort.signal });
    let email, status, submit, error, verify;
    const fields = () => {
      email = form.querySelector('[name="contact[email]"]'); submit = form.querySelector('[type=submit]');
      status = form.querySelector('[data-newsletter-status],[data-popup-status]'); error = form.querySelector('[data-newsletter-field-error],[data-popup-field-error]'); verify = form.querySelector('[data-newsletter-verify],[data-popup-verify]');
    };
    fields();
    if (!email || !submit || !status || form.querySelector('[data-review-submit-status]')) return null;
    const source = form.dataset.pinmeNewsletterSource || options.source || '';
    const idleLabel = submit.dataset.idleLabel || submit.textContent;
    let idleMarkup = submit.innerHTML;
    const returnNonce = options.popup ? crypto.randomUUID() : '';
    const idleAria = submit.getAttribute('aria-label');
    const initialResult = status.dataset.newsletterResult || status.dataset.popupResult;
    const initialMessage = status.textContent.trim();
    if (initialResult === 'success' && !options.acceptInitialResult && (options.popup || form.matches('[data-pinme-inline-newsletter]'))) {
      const idleText = document.createElement('span');
      idleText.textContent = idleLabel;
      idleMarkup = idleText.innerHTML;
    }
    let active = null, protection = null, pending = false, confirmed = false, nativeConfirmed = false, recording = null, fallbackUrl = null, nativeFallback = false, nativePreparing = null;
    function describe(id, add) {
      const ids = (email.getAttribute('aria-describedby') || '').split(/\s+/).filter(value => value && value !== id);
      if (add && id) ids.push(id);
      if (ids.length) email.setAttribute('aria-describedby', [...new Set(ids)].join(' ')); else email.removeAttribute('aria-describedby');
    }
    function clearError() { email.removeAttribute('aria-invalid'); if (error) { error.hidden = true; describe(error.id, false); } describe(status.id, false); }
    function setState(state, message = '') {
      pending = state === 'pending'; form.dataset.newsletterState = state;
      if (options.popup) form.dataset.popupState = state;
      if (pending) form.setAttribute('aria-busy', 'true'); else form.removeAttribute('aria-busy');
      email.readOnly = pending || nativeConfirmed || state === 'success'; submit.disabled = pending || state === 'success';
      if (['pending', 'success'].includes(state)) submit.textContent = state === 'pending' ? submit.dataset.pendingLabel || idleLabel : submit.dataset.joinedLabel || idleLabel;
      else if (options.popup && state === 'verification') submit.textContent = verify?.textContent || idleLabel;
      else submit.innerHTML = idleMarkup;
      if (idleAria) submit.setAttribute('aria-label', options.popup && state === 'verification' ? submit.textContent : state === 'success' ? submit.dataset.joinedLabel || idleAria : idleAria);
      status.hidden = false; status.textContent = decodeMessage(message);
      status.classList.toggle('is-success', state === 'success'); status.classList.toggle('is-error', ['error', 'verification'].includes(state));
      status.classList.toggle('pinme-newsletter-status--quiet', state === 'success' || !message);
      status.setAttribute('role', ['error', 'verification'].includes(state) ? 'alert' : 'status');
      status.setAttribute('aria-live', ['error', 'verification'].includes(state) ? 'assertive' : 'polite');
      if (state === 'success') status.dataset.newsletterResult = 'success'; else status.removeAttribute('data-newsletter-result');
      if (options.popup) { if (state === 'success') status.dataset.popupResult = 'success'; else status.removeAttribute('data-popup-result'); }
      if (verify) verify.hidden = options.popup || !(state === 'verification' && options.allowNativeVerification);
      try { options.onStateChange?.({ state, message: status.textContent, nativeConfirmed, confirmed }); } catch (_) {}
    }
    function warm() {
      if (abort.signal.aborted || confirmed || !form.isConnected) return null;
      if (protection?.form === form) return protection.promise;
      const captcha = window.Shopify?.captcha;
      if (typeof captcha?.protect !== 'function') return null;
      const owned = form, entry = { form: owned, promise: null }; let complete;
      entry.promise = new Promise(resolve => { complete = resolve; }); protection = entry;
      try { captcha.protect(owned, () => complete(form === owned && owned.isConnected && !abort.signal.aborted)); }
      catch (_) { if (protection === entry) protection = null; complete(false); }
      return entry.promise;
    }
    function discard(request) { if (!request) return; clearTimeout(request.timeout); request.controller.abort(); if (active === request) active = null; }
    function complete(message) {
      confirmed = true; clearError(); setState('success', message || status.dataset.successMessage || '');
      if (options.notifyGlobalSubscription !== false) {
        write('localStorage', 'pinme-newsletter-v4-subscribed', 'true');
        try { sessionStorage.removeItem('pinme_newsletter_return_email'); } catch (_) {}
      }
      options.onSuccess?.(status.textContent);
      if (options.notifyGlobalSubscription !== false) document.dispatchEvent(new CustomEvent('pinme:newsletter-subscribed', { detail: { source } }));
    }
    function confirm(message) {
      nativeConfirmed = true;
      if (!form.querySelector('[data-email-interest-config]')) { complete(message); return; }
      if (recording) return;
      clearError(); setState('pending');
      if (!email.value) {
        try { const saved = JSON.parse(read('sessionStorage', 'pinme_newsletter_return_email') || 'null'); if (saved?.source === source && Date.now() - saved.time < 600000) email.value = saved.email; } catch (_) {}
      }
      const context = window.PinmeEmailInterests?.config(form);
      recording = Promise.resolve().then(() => {
        if (!window.PinmeEmailInterests) throw Error('Preferences unavailable');
        return window.PinmeEmailInterests.record(form, {signal:abort.signal});
      }).then(() => { if (!abort.signal.aborted) complete(message); }).catch(() => {
        if (abort.signal.aborted) return;
        setState('error', context?.labels?.preferencesError || status.dataset.errorMessage || '');
        submit.textContent = context?.labels?.retry || idleLabel;
      }).finally(() => { recording = null; });
    }
    function returnDestination(owned, destination) {
      destination.hash = owned.id;
      if (options.popup) {
        destination.searchParams.set('customer_posted', 'true');
        destination.searchParams.set('pn_signup', returnNonce);
        const theme = window.Shopify?.theme;
        if (['unpublished', 'development'].includes(theme?.role) && /^\d+$/.test(String(theme.id))) destination.searchParams.set('preview_theme_id', String(theme.id));
        else if (theme?.role === 'main') destination.searchParams.delete('preview_theme_id');
      }
      return destination;
    }
    function finish(request, state, message, url = null) {
      if (active !== request || abort.signal.aborted) return;
      discard(request);
      if (state === 'success') { confirm(message); return; }
      renew(); fallbackUrl = url;
      if (options.popup && state === 'verification') message = status.dataset.continueMessage || message;
      setState(state, message || status.dataset.errorMessage || '');
      if (state === 'error') { email.setAttribute('aria-invalid', 'true'); describe(status.id, true); }
    }
    // Install before Shopify binds CAPTCHA. Its protected submit wrapper obtains
    // the token first, then calls this form-specific transport instead of navigating.
    function install(owned) {
      const transport = async () => {
        const request = active;
        if (!request || request.native || request.sent || !request.protected || request.form !== owned || form !== owned || !owned.isConnected || abort.signal.aborted) return;
        const body = new URLSearchParams(new FormData(owned));
        if (!['h-captcha-response', 'g-recaptcha-response', 'recaptcha-v3-token'].some(name => body.get(name))) { finish(request, 'verification', status.dataset.verificationMessage); return; }
        const action = new URL(owned.action, location.href);
        if (action.origin !== location.origin) { finish(request, 'verification', status.dataset.errorMessage); return; }
        const returnTo = returnDestination(owned, new URL(location.href));
        if (options.popup) rememberReturn();
        body.set('return_to', returnTo.pathname + returnTo.search + returnTo.hash);
        action.hash = ''; request.sent = true;
        try {
          const response = await fetch(action.href, { method: 'POST', body, credentials: 'same-origin', headers: { Accept: 'text/html' }, signal: request.controller.signal });
          if (active !== request) return;
          const url = new URL(response.url || action.href);
          if (url.origin !== location.origin) { finish(request, 'verification', status.dataset.errorMessage); return; }
          if (/\/challenge(?:\/|$)/.test(url.pathname)) { finish(request, 'verification', status.dataset.verificationMessage, url.href); return; }
          const html = await response.text(); if (active !== request) return;
          const doc = new DOMParser().parseFromString(html, 'text/html'), returnedForm = doc.getElementById(owned.id);
          const result = returnedForm?.querySelector('[data-newsletter-result],[data-popup-result]');
          const resultSource = result?.getAttribute('data-pinme-newsletter-message');
          const sameForm = result && result.closest('form') === returnedForm && (options.popup ? resultSource === source : !resultSource || resultSource === source);
          const state = result?.dataset.newsletterResult || result?.dataset.popupResult;
          if (response.ok && sameForm && state === 'success') { finish(request, 'success', result.textContent.trim()); return; }
          if (sameForm && state === 'error') { finish(request, 'error', result.textContent.replace(/\s+/g, ' ').trim().slice(0, 700)); return; }
          finish(request, 'verification', status.dataset.errorMessage);
        } catch (_) { if (active === request) finish(request, 'verification', status.dataset.errorMessage); }
      };
      Object.defineProperty(owned, 'submit', { value: transport, writable: true, configurable: true });
    }
    // A late CAPTCHA callback retains the retired form and cannot send a retry.
    function renew(enhance = true) {
      protection = null;
      const replacement = form.cloneNode(true), value = email.value;
      replacement.removeAttribute('target'); replacement.classList.add('pinme-newsletter-form'); replacement.setAttribute('data-studio-owned-form', '');
      ['data-hcaptcha-bound', 'data-recaptcha-bound', 'data-cptcha'].forEach(name => replacement.removeAttribute(name));
      replacement.querySelectorAll('.h-captcha,iframe,[name="h-captcha-response"],[name="g-recaptcha-response"],[name="recaptcha-v3-token"],[name="form_key"]').forEach(node => node.remove());
      if (enhance) install(replacement);
      form.replaceWith(replacement); form = replacement; fields(); email.value = value; email.readOnly = false; clearError(); options.onFormChange?.();
    }
    function rememberReturn() {
      if (options.rememberReturn === false) return;
      write('sessionStorage', 'pinme_newsletter_return_email', JSON.stringify({source, nonce:returnNonce, formId:form.id, path:location.pathname, themeId:String(window.Shopify?.theme?.id || ''), email:email.value.trim(), time:Date.now()}));
      [['pinme_newsletter_source', source], ['pinme_form_return_source', source], ['pinme_form_return_scroll_x', String(scrollX)], ['pinme_form_return_scroll_y', String(scrollY)], ['pinme_form_return_time', String(Date.now())]].forEach(([key, value]) => write('sessionStorage', key, value));
    }
    function continueNativeVerification(returnPath) {
      if (!options.allowNativeVerification || pending || active || nativePreparing || confirmed || abort.signal.aborted || !form.checkValidity()) return false;
      let action, destination;
      try { action = new URL(form.action, location.href); destination = new URL(returnPath || location.href, location.href); } catch (_) { return false; }
      if (action.origin !== location.origin || destination.origin !== location.origin) return false;
      renew(false); nativeFallback = true;
      returnDestination(form, destination);
      let returnTo = form.querySelector('[name="return_to"]');
      if (!returnTo) { returnTo = document.createElement('input'); returnTo.type = 'hidden'; returnTo.name = 'return_to'; form.append(returnTo); }
      returnTo.value = destination.pathname + destination.search + destination.hash;
      const owned = form, preparation = { timeout: null }; nativePreparing = preparation;
      submit.disabled = true; form.setAttribute('aria-busy', 'true'); submit.textContent = submit.dataset.pendingLabel || idleLabel;
      const failed = () => {
        if (nativePreparing !== preparation || abort.signal.aborted) return;
        clearTimeout(preparation.timeout); nativePreparing = null; nativeFallback = false; renew(); setState('verification', status.dataset.verificationMessage || '');
      };
      // Bind the real Shopify protection before a normal submit. Hidden forms do
      // not get the focus event that ordinarily starts the platform binding.
      const prepared = warm();
      if (!prepared) { failed(); return false; }
      preparation.timeout = setTimeout(failed, 30000);
      prepared.then(valid => {
        if (nativePreparing !== preparation || abort.signal.aborted || form !== owned || !owned.isConnected) return;
        if (!valid) { failed(); return; }
        clearTimeout(preparation.timeout); nativePreparing = null;
        setState('idle');
        try { owned.requestSubmit(); } catch (_) { nativeFallback = false; renew(); setState('verification', status.dataset.verificationMessage || ''); }
      });
      return true;
    }
    on(root, 'focusin', event => { if (event.target === email) { warm(); window.PinmeEmailInterests?.warm(form)?.catch(() => {}); } });
    on(root, 'invalid', event => {
      if (event.target !== email) return;
      event.preventDefault(); email.setAttribute('aria-invalid', 'true');
      if (error) { error.hidden = false; describe(error.id, true); }
      email.focus({ preventScroll: true }); options.onInvalid?.();
    }, { capture: true });
    on(root, 'input', event => { if (event.target !== email || pending || confirmed || nativeConfirmed) return; clearError(); fallbackUrl = null; setState('idle'); });
    on(root, 'click', event => {
      if (!options.allowNativeVerification || !event.target.closest('[data-newsletter-verify],[data-popup-verify]') || pending) return;
      rememberReturn();
      if (fallbackUrl) { location.assign(fallbackUrl); return; }
      event.preventDefault();
      continueNativeVerification();
    });
    on(root, 'submit', event => {
      if (event.target !== form) return;
      if (pending || nativePreparing || confirmed || !form.checkValidity()) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      if (options.popup && form.dataset.popupState === 'verification' && !nativeFallback) {
        event.preventDefault(); event.stopImmediatePropagation(); rememberReturn();
        if (fallbackUrl) location.assign(fallbackUrl);
        else continueNativeVerification();
        return;
      }
      if (nativeConfirmed) { event.preventDefault(); event.stopImmediatePropagation(); confirm(); return; }
      const request = { form, native: nativeFallback, protected: false, sent: false, controller: new AbortController(), timeout: null };
      active = request; nativeFallback = false; fallbackUrl = null; setState('pending'); options.onPending?.();
      request.timeout = setTimeout(() => finish(request, 'verification', status.dataset.timeoutMessage), 30000);
      if (request.native) { rememberReturn(); return; }
      event.preventDefault(); event.stopImmediatePropagation();
      const prepared = warm();
      if (!prepared) { finish(request, 'verification', status.dataset.verificationMessage); return; }
      prepared.then(valid => {
        if (active !== request || abort.signal.aborted || !request.form.isConnected) return;
        if (!valid) { finish(request, 'verification', status.dataset.verificationMessage); return; }
        request.protected = true;
        try { request.form.submit(); } catch (_) { finish(request, 'verification', status.dataset.verificationMessage); }
      });
    }, { capture: true });
    // Retire any form already captured by an earlier platform listener.
    renew(); setState('idle');
    const controller = {
      get form() { return form; }, get email() { return email; }, get confirmed() { return confirmed; },
      initialResult: initialResult ? { state: initialResult, message: initialMessage } : null,
      warm, confirm, continueNativeVerification,
      showError(message) { setState('error', message); email.setAttribute('aria-invalid', 'true'); describe(status.id, true); },
      cancel() { if (active || nativePreparing) { if (nativePreparing) { clearTimeout(nativePreparing.timeout); nativePreparing = null; } nativeFallback = false; discard(active); renew(); setState('idle'); } },
      destroy() { controller.cancel(); abort.abort(); controllers.delete(root); }
    };
    on(window, 'pagehide', controller.cancel);
    controllers.set(root, controller);
    if (options.acceptInitialResult && initialResult === 'success') confirm(initialMessage);
    else if (options.acceptInitialResult && initialResult === 'error') controller.showError(initialMessage);
    return controller;
  }
  window.PinmeNewsletterForm = { enhance, destroy(scope) { controllers.forEach((controller, root) => { if (scope === root || scope.contains(root)) controller.destroy(); }); } };
})();


